import { useState } from "react";
import { Download, Eye, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { descargarBlob, nombreDesdeContentDisposition } from "@/lib/download";

/**
 * Consultar / descargar el PDF de una solicitud de Telecom. Por defecto, el de
 * la Solicitud de Certificado (TEL-14, formato NPM-T0081-E4); la Carta Aduana
 * pasa su propio endpoint (formato NPM-T0037-R12). El backend lo genera al
 * vuelo con la información vigente y aplica la misma visibilidad que el
 * detalle; aquí sólo se pide el blob.
 *
 * «Ver PDF» no usa un object URL: un blob no tiene nombre y, al guardar desde
 * el visor de PDF del navegador, el archivo se llamaba como el UUID del blob.
 * El backend publica el PDF unos minutos en una URL presignada de MinIO
 * (`…/visor`) cuya ruta termina en el nombre legible, como los documentos
 * subidos.
 */
export function PdfSolicitudAcciones({
  solicitudId,
  nombreArchivo,
  disponible,
  endpoint = "pdf-solicitud",
  mensajeNoDisponible = "Disponible cuando la solicitud sea aprobada en la etapa Solicitud.",
}: {
  solicitudId: string;
  nombreArchivo: string;
  disponible: boolean;
  /** Último segmento de la ruta bajo /api/sector/telecom/solicitudes/{id}/. */
  endpoint?: "pdf-solicitud" | "pdf-carta-aduana";
  mensajeNoDisponible?: string;
}) {
  const [cargando, setCargando] = useState<"ver" | "descargar" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [visor, setVisor] = useState<{ url: string; nombre: string } | null>(null);

  if (!disponible) {
    return <span className="text-sm text-fg-muted">{mensajeNoDisponible}</span>;
  }

  const pedir = async (accion: "ver" | "descargar") => {
    setCargando(accion);
    setError(null);
    try {
      const base = `/api/sector/telecom/solicitudes/${solicitudId}/${endpoint}`;
      if (accion === "descargar") {
        const resp = await api.get<Blob>(base, { responseType: "blob" });
        const nombre =
          nombreDesdeContentDisposition(resp.headers["content-disposition"] as string | undefined) ?? nombreArchivo;
        descargarBlob(resp.data, nombre);
      } else {
        const resp = await api.get<{ url: string; nombreArchivo: string }>(`${base}/visor`);
        setVisor({ url: resp.data.url, nombre: resp.data.nombreArchivo });
      }
    } catch (e) {
      setError((await detalleDelError(e)) ?? "No se pudo generar el PDF de la solicitud. Intenta de nuevo.");
    } finally {
      setCargando(null);
    }
  };

  const clase =
    "inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2 disabled:opacity-50";

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-2">
        <button type="button" className={clase} disabled={cargando !== null} onClick={() => pedir("ver")}>
          {cargando === "ver" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
          Ver PDF
        </button>
        <button type="button" className={clase} disabled={cargando !== null} onClick={() => pedir("descargar")}>
          {cargando === "descargar" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Descargar
        </button>
      </div>
      {error && <p className="text-sm text-estado-err">{error}</p>}

      {visor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2">
          <div className="flex h-[95vh] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-surface shadow-overlay">
            <div className="flex items-center justify-between border-b px-4 py-2">
              <div className="min-w-0">
                <h3 className="truncate text-sm font-semibold text-fg">{visor.nombre}</h3>
                <p className="text-xs text-fg-subtle">Generado con la información vigente de la solicitud</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={cargando !== null}
                  onClick={() => pedir("descargar")}
                  className="inline-flex cursor-pointer items-center gap-1 rounded border px-2.5 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
                >
                  <Download className="h-3 w-3" /> Descargar
                </button>
                <button
                  type="button"
                  onClick={() => setVisor(null)}
                  className="cursor-pointer rounded p-1 transition hover:bg-surface-2"
                  aria-label="Cerrar"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <iframe key={visor.url} title={visor.nombre} src={visor.url} className="h-full w-full flex-1 bg-surface-2" />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Muestra el `detail` del ProblemDetail (p. ej. los datos obligatorios que
 * faltan). Con responseType "blob" llega como blob y hay que leerlo; desde
 * `…/visor` llega ya como JSON. Sólo para 409, que es donde el backend explica
 * el motivo.
 */
async function detalleDelError(e: unknown): Promise<string | null> {
  const resp = (e as { response?: { status?: number; data?: unknown } }).response;
  if (resp?.status !== 409) return null;
  if (!(resp.data instanceof Blob)) return (resp.data as { detail?: string } | undefined)?.detail ?? null;
  try {
    const cuerpo = JSON.parse(await resp.data.text()) as { detail?: string };
    return cuerpo.detail ?? null;
  } catch {
    return null;
  }
}
